import { useEffect, useState, useRef } from "react"
import "./UpsellPopUp.css"
import { useShippingPageContext } from "../../../v2/ShippingV2/providers/ShippingPageProvider"
import { useCartContext } from "../../../../customHooks/useCart"
import { translate } from "../../../../utils/translator"
import { useAppContext } from "../../../../providers/AppProvider"
import { MainTracker } from "../../../../PixelTrackers/MainTracker"

// Custom SVG icons to replace Lucide
const XIcon = () => (
  <svg className="x-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
)

const CheckIcon = () => (
  <svg className="check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
)

const ChevronDownIcon = ({ rotated = false }) => (
  <svg
    className={`chevron-icon${rotated ? " rotated" : ""}`}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    style={{ transition: "transform 0.2s" }}
  >
    <path
      d="M6 8l4 4 4-4"
      stroke="#27ae60"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FLOWZ_TREATMENT_COST = 4.20;
const DEFAULT_FUNNEL_TREATMENT_COST = 18.90;
const CANNABIS_ID_COST = 6.90;
const DELIVERY_COST = 8;



interface UpsellPopUpProps {
  handleDeliveryOptionChange: (type: "only_prescription" | "express" | "regular" | "packstation") => Promise<void>
  abTestVariant?: 'A' | 'B' | 'C' | null
}

export const UpsellPopUp = ({ handleDeliveryOptionChange, abTestVariant = null }: UpsellPopUpProps) => {

  const [isMedikamenteExpanded, setIsMedikamenteExpanded] = useState(false);
  const [isEinmaligeExpanded, setIsEinmaligeExpanded] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const [interactedBeforeClose, setInteractedBeforeClose] = useState(false);
  const [openedAt, setOpenedAt] = useState<number | null>(null);

  const { setStickyNotification } = useAppContext();

  const {
    setShowUpsellPopUp,
    isFlowzzMoreExpensive,
    upsellSaveAmount,
    upsellProducts,
    finalAlternativeProducts,
    handleShippingSubmit,
    currentFormData,
    setPreSelectedExternalPharmacy,
    showUpsellOnShippingPage,
    language,
    upgradePrescriptionOnlyToPremium,
    setWasUpgradedToPremium,
    setIsExpressPrescriptionReview,
    isOriginalCartFlowFlowzz,
    setExpressDeliveryState,
    setExpressStockCode,
    setUpsellPopupDismissed,
    setCouponWidgetState,
  } = useShippingPageContext();

  const {
    getCart,
    saveCart
  } = useCartContext();

  const cart = getCart();
  const cartTreatmentFee = cart.treatment_fee || DEFAULT_FUNNEL_TREATMENT_COST;
  
  const upsellProductsForTracking = upsellProducts.map(product => ({
    title: product.title,
    product_in_stock: product.product_in_stock,
    item_price: product.item_price,
    quantity: product.quantity,
    product_item_id: product.product_item_id,
    flowz_price: product.flowz_price,
  }));

  const finalAlternativeProductsForTracking = Object.values(finalAlternativeProducts).map(product => ({
    title: product.title,
    product_in_stock: product.product_in_stock,
    item_price: product.item_price,
    quantity: product.quantity,
    product_item_id: product.product_item_id,
    flowz_price: product.flowz_price,
  }));
  
  useEffect(() => {
    const now = Date.now();
    setOpenedAt(now);
    setUpsellPopupDismissed(true);

    // Legacy tracking (keep for backwards compatibility)
    window.eventsTracking.track({ e: "Upsell Popup appears", p: { totalPriceFlowz: totalCost, 
      originalFlow: isOriginalCartFlowFlowzz? "flowzz" : "doktorABC",
      isPopupAppearFromNext: !showUpsellOnShippingPage,
      upsellSaveAmount,
      totalPricePremium: sumOfPremiumProducts,
      premiumProducts: upsellProductsForTracking,
      alternativeProducts: finalAlternativeProductsForTracking } });
    MainTracker.track('price', 'Upsell Pop Up', { 'upsellSaveAmount': upsellSaveAmount, 
      'sumOfFlowzPrices': sumOfFlowzPrices,
      'originalTreatmentCost': isOriginalCartFlowFlowzz ? FLOWZ_TREATMENT_COST : cartTreatmentFee,
      'cannabisIdCost': CANNABIS_ID_COST,
      'deliveryCost': DELIVERY_COST,
      'sumOfPremiumProducts': sumOfPremiumProducts, 'totalCost': totalCost, }); // sumOfPremiumProducts  - totalCost = upsellSaveAmount 
      // sumOfFlowzPrices + originalTreatmentCost + cannabisIdCost + deliveryCost = totalCost
    MainTracker.track('pv', 'View Pop Up', { 'Pop Up Name': 'Upsell Pop Up' });

    // Upsell_Popup_Shown event
    MainTracker.track('custom', 'Upsell_Popup_Shown', {
      variant,
      price_scenario: priceScenario,
      page_context: pageContext,
    });

    if (Object.keys(finalAlternativeProducts).length > 0) {
      setIsMedikamenteExpanded(true);
    }

    // Track interactions (scroll, hover, click)
    const handleInteraction = () => setInteractedBeforeClose(true);
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      // e.target.scrollTop > 0 means user scrolled down
      if (target.scrollTop > 0 && !hasScrolledRef.current) {
        hasScrolledRef.current = true;
        setInteractedBeforeClose(true);
        window.eventsTracking.track({ e: "Upsell Popup scrolled" });
      }
    };
  
    const node = modalContentRef.current;
    if (node) {
      node.addEventListener("scroll", handleScroll);
      node.addEventListener("mouseenter", handleInteraction);
      node.addEventListener("click", handleInteraction);
    }
    return () => {
      if (node) {
        node.removeEventListener("scroll", handleScroll);
        node.removeEventListener("mouseenter", handleInteraction);
        node.removeEventListener("click", handleInteraction);
      }
    };
  }, [])

  const sumOfPremiumProducts = upsellProducts.reduce((total, product) => {
    // Check if an alternative product should be used
    const alternative = finalAlternativeProducts[product.product_item_id!];
    if (alternative) {
      // Use the alternative product's price
      return total + (alternative.item_price || 0);
    }
    // If no alternative, use the original product's price, but only if it's in stock for premium delivery
    if (product.product_in_stock) {
      return total + (product.item_price || 0);
    }
    // If the product is out of stock and has no alternative, it adds nothing to the premium total
    return total;
  }, 0);

  const sumOfFlowzPrices = upsellProducts.reduce((total, product) => total + (product.flowz_price || 0), 0);
  const totalCost = sumOfFlowzPrices + (isOriginalCartFlowFlowzz ? FLOWZ_TREATMENT_COST : cartTreatmentFee) + CANNABIS_ID_COST + DELIVERY_COST;

  // Tracking helpers (defined here so they're available to all handlers)
  const variant = abTestVariant || null;
  const hasAlternativeProducts = Object.keys(finalAlternativeProducts).length > 0;
  const priceScenario = hasAlternativeProducts ? null : ( (isFlowzzMoreExpensive ? 'expensive' : 'cheaper') );
  const pageContext = 'shipping';

  const handleContinueToPayment = async () => {
    const timeToClick = openedAt ? (Date.now() - openedAt) : 0;
    
    // Legacy tracking
    window.eventsTracking.track({ e: "Upsell Popup rejected", p: { timeSpentMs: timeToClick } });
    
    // New Mixpanel tracking - Upsell_CTA_Clicked (user chose to keep prescription only)
    MainTracker.track('custom', 'Upsell_CTA_Clicked', {
      variant,
      price_scenario: priceScenario,
      time_to_cta_click_ms: timeToClick,
      cta_label: 'keep prescription only',
      popup_visible_before_click_ms: timeToClick,
    });
    
    if (showUpsellOnShippingPage) {
      setShowUpsellPopUp(false);
    } else {
      // Call handleShippingSubmit with skipUpsell = true to bypass the upsell check
      handleShippingSubmit(currentFormData!, false, false, true);
    }
  };


  const handleUpgradeToPremium = async () => {
    const timeToClick = openedAt ? (Date.now() - openedAt) : 0;
    
    // Legacy tracking
    window.eventsTracking.track({ e: "Upsell Popup accepted", 
      p: { timeSpentMs: timeToClick } 
    });

    // New Mixpanel tracking - Upsell_CTA_Clicked (user chose to upgrade order)
    MainTracker.track('custom', 'Upsell_CTA_Clicked', {
      variant,
      price_scenario: priceScenario,
      time_to_cta_click_ms: timeToClick,
      cta_label: 'upgrade order',
      popup_visible_before_click_ms: timeToClick,
    });

    MainTracker.track('custom', 'upsale_accepted', {
      user_id: window.eventsTracking?.getData?.("customerID") || null,
      order_id: cart.id || null,
      upsell_type: 'premium',
      variant,
      price_scenario: priceScenario,
      savings_euros: upsellSaveAmount,
    });

    const productItemIdsToRemove = Object.keys(finalAlternativeProducts).map(key => parseInt(key));
    const alternativeProductItems = Object.values(finalAlternativeProducts).map(product => (product.product_item_id!));

    upgradePrescriptionOnlyToPremium(productItemIdsToRemove, alternativeProductItems)
    .then(async (response: any) => {
      const cartView = response.data.cartView as CartView;
      const isExpressFlow = response.data.isExpressFlow;
      // Update the cartView
      if (cartView) {
        saveCart({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking });
        setExpressStockCode(cartView.expressStockCode);
      }

      // Clear stale coupon state so handleDeliveryOptionChange won't re-apply the old coupon
      setCouponWidgetState((current) => ({
        ...current,
        inputCode: "",
        errorMsg: "",
        exposeExtraEnterContainer: false,
      }));

      setExpressDeliveryState((prevState) => ({
        ...prevState,
        isAvailable: isExpressFlow,
        deliveryPriceLoading: isExpressFlow,
        isEnabled: false
      }));

      setPreSelectedExternalPharmacy(null);
      setIsExpressPrescriptionReview(false);
      setWasUpgradedToPremium(true);
      MainTracker.superProperties({ "Purchase Type": "Premium" });

      if (showUpsellOnShippingPage) {
        setShowUpsellPopUp(false);
      } else {
        // Call handleShippingSubmit with skipUpsell = true to bypass the upsell check
        // Pass wasUpgradedToPremium = true directly instead of relying on state
        handleShippingSubmit(currentFormData!, false, false, true, true);
      }
      await handleDeliveryOptionChange("regular");
    })
    .catch((error) => {
      setStickyNotification({
        show: true,
        level: "error",
        showInfoIcon: true,
        title: translate(language, "default", "ServerError/title") as string,
        content: translate(language, "default", "ServerError/info") as string,
        autoFade: true,
      });
    });
  };

  const handleCloseUpsellPopUp = () => {
    const timeToClose = openedAt ? (Date.now() - openedAt) : 0;
    
    // Legacy tracking
    window.eventsTracking.track({ e: "Upsell Popup closed from x button", p: { timeSpentMs: timeToClose } });
    
    // New Mixpanel tracking - Upsell_Popup_Closed
    MainTracker.track('custom', 'Upsell_Popup_Closed', {
      variant,
      price_scenario: priceScenario,
      close_reason: 'x_button',
      time_to_close_ms: timeToClose,
      interacted_before_close: interactedBeforeClose,
    });
    
    setShowUpsellPopUp(false);
  }

  const handleClickMedicationToggle = () => {
    setIsMedikamenteExpanded(!isMedikamenteExpanded);
    window.eventsTracking.track({ e: "Upsell Popup medication toggle" });
  }

  const handleClickOnePaymentToggle = () => {
    setIsEinmaligeExpanded(!isEinmaligeExpanded);
    window.eventsTracking.track({ e: "Upsell Popup one payment toggle" });
  }

  return (
    <div className="modal-overlay">
      <div className="modal-container" ref={modalContentRef}>
        {/* Header */}
        <div className="modal-header">
          <button onClick={handleCloseUpsellPopUp} className="close-button">
            <XIcon />
          </button>
          {isFlowzzMoreExpensive ? (
            <>
              <h2 className="headline-desktop">🎁 Mehr für weniger. Premium wählen.</h2>
              <h2 className="headline-mobile">
                Mehr für weniger.<br />
                Premium wählen. 🎁
              </h2>
            </>
          ) : (
            <>
              <h2 className="headline-desktop">Eine Zahlung. Schnelle Lieferung.<br/>Premium wählen 🎁</h2>
              <h2 className="headline-mobile">Ei ne Zahlung. Schnelle <br/> Lieferung. Premium wählen 🎁 </h2>
            </>
          )}
        </div> 

        {/* Content */}
        <div className="modal-content">

          {/* Feature Rows */}
          <div id="feature-header-rows" className="feature-rows">
            <div className="feature-row">
              <div className="feature-cell bg-header">
                <div className="feature-text headline">Features</div>
              </div>
              <div className="feature-cell premium-header ">
                <div className="premium-text green headline">Premium Service</div>
              </div>
              <div className="feature-cell bg-header">
                <div className="headline-text">Nur Rezept</div>
              </div>
            </div>

            {/* Einmalige Zahlung */}
            <>
              <div
                id="time-payment-row"
                className="feature-row clickable-row"
                onClick={handleClickOnePaymentToggle}
                style={{ cursor: "pointer", display: "contents" }}
              >
                <div className="feature-cell">
                  <div className="feature-text">Einmalige Zahlung</div>
                  <div className="feature-text"></div>
                  <div className="feature-subtext" style={{ display: "flex", alignItems: "center" }}>
                    <ChevronDownIcon rotated={isEinmaligeExpanded} />
                    <span>Was ist das?</span>
                  </div>
                </div>
                <div className="feature-cell premium">
                  <div className="icon-container">
                    <CheckIcon />
                  </div>
                </div>
                <div className="feature-cell">
                  <div className="icon-container">
                    <XIcon />
                  </div>
                </div>
              </div>
              {isEinmaligeExpanded && (
                <div className="dropdown-content" style={{ gridColumn: "1 / -1" }}>
                  <div className="dropdown-header">
                    <div className="dropdown-text">
                      <div>
                        <strong>Eine Zahlung:</strong> Einmalige Vorauszahlung an der Kasse (Rezept + Medikamente).
                      </div>
                      <div>
                        <strong>Zwei Zahlungen:</strong> Eine für das Rezept, später für die Medikamente in der
                        Apotheke.
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsEinmaligeExpanded(false)
                      }}
                      className="dropdown-close"
                    >
                      <XIcon />
                    </button>
                  </div>
                </div>
              )}
            </>

            {/* VIP Kundendienst */}
            <div id="vip-customer-support-row" className="feature-row">
              <div className="feature-cell">
                <div className="feature-text">VIP Kundendienst</div>
              </div>
              <div className="feature-cell premium">
                <div className="icon-container">
                  <CheckIcon />
                </div>
              </div>
              <div className="feature-cell">
                <div className="icon-container">
                  <XIcon />
                </div>
              </div>
            </div>

            {/* Behandlung */}
            <div id="treatment-row" className="feature-row">
              <div className="feature-cell">
                <div className="feature-text">Behandlung</div>
              </div>
              <div className="feature-cell premium">
                <div className="premium-text">Gratis</div>
              </div>
              <div className="feature-cell">
                {isOriginalCartFlowFlowzz ? (
                  <div className="price-text">{FLOWZ_TREATMENT_COST} €</div>
                ) : (

                  <div className="price-text">{cartTreatmentFee} €</div>
                )}
              </div>
            </div>

            {/* Cannabis-Ausweis */}
            <div id="cannabis-id-row" className="feature-row">
              <div className="feature-cell">
                <div className="feature-text">Cannabis-Ausweis</div>
              </div>
              <div className="feature-cell premium">
                <div className="premium-text">Gratis</div>
              </div>
              <div className="feature-cell">
                <div className="price-text">{CANNABIS_ID_COST} €</div>
              </div>
            </div>

            {/* Lieferung */}
            <div id="delivery-row" className="feature-row">
              <div className="feature-cell">
                <div className="feature-text">Lieferung</div>
              </div>
              <div className="feature-cell premium">
                <div className="premium-text">Gratis</div>
              </div>
              <div className="feature-cell">
                <div className="price-text">4-8 €</div>
              </div>
            </div>

            {/* Lieferzeit */}
            <div id="delivery-time-row" className="feature-row">
              <div className="feature-cell">
                <div className="feature-text">Lieferzeit</div>
              </div>
              <div className="feature-cell premium">
                <div className="premium-text">24-48h</div>
              </div>
              <div className="feature-cell">
                <div className="price-text">Je nach Apotheke</div>
              </div>
            </div>


            {/* Medikamente */}
            <>
              <div
                id="medication-row"
                className="feature-row clickable-row"
                onClick={handleClickMedicationToggle}
                style={{ cursor: "pointer", display: "contents" }}
              >
                <div className="feature-cell">
                  <div className="feature-text">Medikamente</div>
                  <div className="feature-subtext" style={{ display: "flex", alignItems: "center" }}>
                    <ChevronDownIcon rotated={isMedikamenteExpanded} />
                    <span>Warenkorb-Übersicht</span>
                  </div>
                </div>
                <div className="feature-cell premium">
                  <div className="premium-text">{sumOfPremiumProducts.toFixed(2).replace('.', ',')} €</div>
                </div>
                <div className="feature-cell">
                  <div className="price-text">{sumOfFlowzPrices.toFixed(2).replace('.', ',')} €</div>
                  {isOriginalCartFlowFlowzz && <div className="price-subtext">(ausgewählte Apotheke)</div>}
                </div>
              </div>
              {isMedikamenteExpanded && (
                <div className="products-content" style={{ gridColumn: "1 / -1" }}>
                  <button
                    onClick={() => setIsMedikamenteExpanded(false)}
                    className="dropdown-close"
                    style={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}
                  >
                    <XIcon />
                  </button>
                  {upsellProducts && upsellProducts.length > 0 ? (
                    [
                    upsellProducts.map((product, idx) => (
                      <div className={`upsell-product-row${idx === upsellProducts.length - 1 && Object.keys(finalAlternativeProducts).length > 0 ? ' last-before-alternative' : ''}`} key={`row-${idx}`}>
                        {/* Column 1: image and title */}
                        <div className="upsell-product-info" key={`info-${idx}`}>
                          {product.image_src && (
                            <img src={product.image_src} alt={product.title} className="upsell-product-image" />
                          )}
                          <div>
                            <div className={product.product_in_stock ? "upsell-product-title" : "upsell-product-title out-of-stock-title"}>{product.title}</div>
                            {product.thc_percentage && product.cbd_percentage && (
                              <div className="upsell-product-details">
                                THC: {product.thc_percentage}% | CBD &lt;{product.cbd_percentage}%
                              </div>
                            )}
                            {product.product_in_stock === false && (
                              <div className="upsell-product-unavailable">Nicht verfügbar bei Premium</div>
                            )}
                          </div>
                        </div>
                        {/* Column 2: Premium price */}
                        <div className="upsell-product-price" key={`premium-${idx}`}>
                          {product.item_price !== undefined && (
                            <span className={product.product_in_stock ? '' : 'out-of-stock-message'}>
                              {product.product_in_stock
                                ? `${product.item_price.toFixed(2)} €`
                                : 'Nicht verfügbar bei Premium'}
                            </span>
                          )}
                        </div>
                        {/* Column 3: Flowz price */}
                        <div className="upsell-product-price" key={`flowz-${idx}`}>
                          {product.flowz_price !== undefined && (
                            <span className={product.product_in_stock ? '' : 'out-of-stock-price'}>
                              {product.flowz_price.toFixed(2)} €
                            </span>
                          )}
                        </div>
                      </div>
                    )),
                    Object.keys(finalAlternativeProducts).length > 0 && (
                      <div className="alternative-section" style={{ gridColumn: '1 / -1' }} key="alternative-section">
                        <div className="alternative-header">Alternative</div>
                        <div className="alternative-products-grid">
                          {Object.values(finalAlternativeProducts).map((product, idx) => (
                            <div className="upsell-product-row" key={`alt-row-${idx}`}>
                              <div className="upsell-product-info" key={`alt-info-${idx}`}>
                                {product.image_src && (
                                  <img
                                    src={product.image_src}
                                    alt={product.title}
                                    className="upsell-product-image"
                                  />
                                )}
                                <div className="upsell-product-details-container">
                                  <div className="upsell-product-title">{product.title}</div>
                                  {product.thc_percentage && product.cbd_percentage && (
                                    <div className="upsell-product-details">
                                      THC: {product.thc_percentage}% | CBD &lt;{product.cbd_percentage}%
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="upsell-product-price" key={`alt-premium-${idx}`}>
                                {product.item_price !== undefined && <span>{product.item_price.toFixed(2)} €</span>}
                              </div>
                              <div className="upsell-product-price" key={`alt-flowz-${idx}`}>
                                <span className="long-dash"></span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                  ]
                  ) : (
                    <div>Keine Produkte im Warenkorb.</div>
                  )}
                </div>
              )}
            </>


            {/* Gesamtpreis */}
            <div id="total-price-row" className="feature-row">
              <div className="feature-cell total">
                <div className="feature-text">
                  Gesamtpreis
                </div>
              </div>
              <div className="feature-cell total premium">
                <div className="price-large green">{sumOfPremiumProducts.toFixed(2).replace('.', ',')} €</div>
              </div>
              <div className="feature-cell total">
                <div className="price-text">{totalCost.toFixed(2).replace('.', ',')} €</div>
                <div className="price-subtext">inkl. Lieferkosten</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {isFlowzzMoreExpensive? <button className="btn btn-primary" onClick={handleUpgradeToPremium}><span className="big-checkmark">✓</span> Upgraden und {upsellSaveAmount} € sparen</button> : <button className="btn btn-primary" onClick={handleUpgradeToPremium}><span className="big-checkmark">✓</span> Premium wählen und 1x zahlen</button>}
            <button className="btn btn-secondary" onClick={handleContinueToPayment}>Beim Rezept bleiben</button>
          </div>
        </div>
      </div>
    </div>
  )
}